import { CallSplit, Remove } from "@mui/icons-material";
import {
  Stack,
  Autocomplete,
  TextField,
  IconButton,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  Dimension,
  DimensionType,
  FilterOption,
  isOffered,
  Operation,
  Value,
} from "../../../models/Explorer";
import { useExplorerStore } from "../../../data/ExplorerStore";
import { useGlossary } from "../../../hooks/useGlossary";
import { FeatureFlags } from "../../../util/featureFlags";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";

interface Props {
  metricGroupIndex: number;
  index: number;
  dimensionType: DimensionType;
}

export default function DimensionSelect({
  metricGroupIndex,
  index,
  dimensionType,
}: Props) {
  const dimensionsOfType = useExplorerStore(
    ({ metricGroups }) => metricGroups[metricGroupIndex][dimensionType].length
  );
  const dimension = useExplorerStore(
    ({ metricGroups }) => metricGroups[metricGroupIndex][dimensionType][index]
  );
  const updateDimension = useExplorerStore(
    ({ updateDimension }) =>
      (dimension: Dimension) =>
        updateDimension(metricGroupIndex, dimensionType, index, dimension)
  );
  const removeDimension = useExplorerStore(
    ({ removeDimension }) =>
      () =>
        removeDimension(metricGroupIndex, dimensionType, index)
  );
  const filters = useExplorerStore(({ filters }) => filters);
  // Definitions in the picker itself: this is where someone chooses between
  // PickBallDefense, PickBallSimpleDefense, PickDefense and PickCoverage.
  const { byName: glossary } = useGlossary();
  const showSplits =
    dimensionType === "filters" || (!!dimension.params && dimension.params > 0);
  const getFilterOption = useCallback(
    (
      filterName: string | undefined = dimension.name?.value
    ): FilterOption | undefined => {
      if (!filters || !filterName) {
        return undefined;
      }
      return filters[filterName];
    },
    [dimension.name, filters]
  );

  const [filterOption, setFilterOption] = useState<FilterOption | undefined>(
    getFilterOption()
  );

  // Hidden filters still resolve on the backend, so a saved query that uses one
  // must keep it selectable here — otherwise loading that query would silently
  // blank its filter. Only the list of *new* choices is narrowed.
  const filterNames = useMemo(() => {
    if (!filters) return [""];
    const offered = Object.keys(filters).filter(
      (name) =>
        isOffered(filters[name]) &&
        // The provider split is a dev comparison tool, never a product choice.
        (name !== "Source" || FeatureFlags.sourcePicker),
    );
    const current = dimension.name?.value;
    if (current && filters[current] && !offered.includes(current)) {
      offered.push(current);
    }
    return [""].concat(offered);
  }, [filters, dimension.name?.value]);

  const inputSize = "small" as const;

  useEffect(() => {
    const newFilterOption = getFilterOption();
    setFilterOption(newFilterOption);
  }, [getFilterOption]);

  if (!filters) return null;

  const onFilterChange = (
    event: ChangeEvent<unknown>,
    value: string | null
  ) => {
    const dimensionForUpdate = Object.assign({}, dimension);
    dimensionForUpdate.name = {
      value: value ?? "",
      label: value ?? "",
      index: 0,
    };
    const newFilterOption = getFilterOption(value ?? undefined);
    setFilterOption(newFilterOption);
    if (!newFilterOption?.ops.some((op) => op.value === dimension.op?.value)) {
      dimensionForUpdate.op = newFilterOption?.ops[0];
    }
    const values = dimensionForUpdate.values;
    let applicableValues = Array.isArray(values) ? values.slice() : [];
    if (Array.isArray(applicableValues)) {
      applicableValues = applicableValues.filter((v) =>
        newFilterOption?.values.some((fov) => fov.value === v.value)
      );
    }
    dimensionForUpdate.values = applicableValues;
    updateDimension(dimensionForUpdate);
  };

  const onOpsChange = (
    event: ChangeEvent<unknown>,
    value: Operation | null
  ) => {
    const dimensionForUpdate = Object.assign({}, dimension);
    dimensionForUpdate.op = value ?? undefined;
    updateDimension(dimensionForUpdate);
  };

  const splitDimension = () => {
    const dimensionForUpdate = Object.assign({}, dimension);
    dimensionForUpdate.params = 1;
    if (!Array.isArray(dimensionForUpdate.values)) {
      dimensionForUpdate.values = [];
    }
    updateDimension(dimensionForUpdate);
  };

  const valueSelect = !!dimension.name &&
    !!filterOption &&
    showSplits &&
    (filterOption.type === "values" ? (
      <MultiValueSelect
        filterOption={filterOption}
        dimension={dimension}
        updateDimension={updateDimension}
        inputSize={inputSize}
      />
    ) : filterOption.type === "date" ? (
      <DateValueSelect
        filterOption={filterOption}
        dimension={dimension}
        updateDimension={updateDimension}
      />
    ) : (
      <NumberValueSelect
        filterOption={filterOption}
        dimension={dimension}
        updateDimension={updateDimension}
      />
    ));

  return (
    <Box
      mt={1}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        p: 1,
        backgroundColor: "grey.50",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Autocomplete
          onChange={onFilterChange}
          id="filterSelect"
          options={filterNames}
          value={dimension.name?.value ?? ""}
          renderOption={(props, option) => {
            const entry = option ? glossary.get(option) : undefined;
            const hint =
              entry && entry.kind === "filter"
                ? entry.description ?? entry.expression
                : undefined;
            return (
              <li {...props} key={option}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: "0.8rem" }}>{option}</Typography>
                  {hint && (
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: "rgba(0,0,0,0.55)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {hint}
                    </Typography>
                  )}
                </Box>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField {...params} variant="outlined" placeholder="Filter" size={inputSize} />
          )}
          sx={{ flex: 1, minWidth: 0 }}
          autoComplete
          autoSelect
          disableClearable={
            index > 0 || dimensionsOfType > 1 || dimension.name?.value === ""
          }
        />
        {!!dimension.name && !!filterOption && showSplits && (
          <Autocomplete
            onChange={onOpsChange}
            id="opSelect"
            options={filterOption.ops}
            value={dimension.op ?? filterOption?.ops[0]}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" placeholder="Op" size={inputSize} />
            )}
            isOptionEqualToValue={(option, value) =>
              value.value === "" || option.value === value.value
            }
            disableClearable
            sx={{ width: { xs: "80px", sm: "110px" }, flexShrink: 0 }}
            autoSelect
          />
        )}
        {!!dimension.name && !!filterOption && !showSplits && (
          <IconButton onClick={splitDimension} size="small">
            <CallSplit fontSize="small" />
          </IconButton>
        )}
        {index > 0 || dimensionsOfType > 1 ? (
          <IconButton onClick={removeDimension} size="small">
            <Remove fontSize="small" />
          </IconButton>
        ) : (
          <IconButton disabled sx={{ opacity: 0 }} size="small">
            <Remove fontSize="small" />
          </IconButton>
        )}
      </Stack>
      {valueSelect && (
        <Box mt={0.75}>
          {valueSelect}
        </Box>
      )}
      {/* Without this, selecting a discontinued filter for a current season just
          returns nothing, with no indication that the tagging stopped years ago. */}
      {filterOption?.seasonCoverage && (
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 0.5, color: "warning.dark" }}
        >
          {filterOption.seasonCoverage}
        </Typography>
      )}
    </Box>
  );
}

interface ValueSelectProps {
  dimension: Dimension;
  updateDimension: (d: Dimension) => void;
  filterOption: FilterOption;
}

function MultiValueSelect({
  dimension,
  updateDimension,
  filterOption,
  inputSize = "medium",
}: ValueSelectProps & { inputSize?: "small" | "medium" }) {
  const onChange = (event: ChangeEvent<unknown>, value: Value[] | null) => {
    const dimensionForUpdate = Object.assign({}, dimension);
    dimensionForUpdate.values = value ?? undefined;
    updateDimension(dimensionForUpdate);
  };
  const filters = useExplorerStore(({ filters }) => filters);

  // Player filter support - use shared player list
  const playerFilterOptions = useExplorerStore(
    ({ playerFilterOptions }) => playerFilterOptions
  );

  const isPlayerFilter = filterOption.player_id === true;
  const isLoading = isPlayerFilter && playerFilterOptions?.loading;
  const loadError = isPlayerFilter ? playerFilterOptions?.error : undefined;

  // Determine which values to use
  const filterValues = useMemo(() => {
    if (isPlayerFilter) {
      return playerFilterOptions?.values ?? [];
    }
    return filterOption.values;
  }, [isPlayerFilter, playerFilterOptions?.values, filterOption.values]);

  const isDependentFilter = useMemo(
    () =>
      dimension.op &&
      (dimension.op?.value === "as" || dimension.op?.value === "not as"),
    [dimension]
  );

  const filterOptions = useMemo(() => {
    if (!isDependentFilter || !filters) {
      return filterValues.slice().sort((a, b) => (a.label < b.label ? 1 : -1));
    } else {
      return Object.keys(filters).map((f) => ({
        label: f,
        value: f,
      }));
    }
  }, [isDependentFilter, filterValues, filters]);

  if (!Array.isArray(dimension.values)) return null;

  return (
    <Box>
      <Autocomplete
        onChange={onChange}
        id="valuesSelect"
        multiple
        options={filterOptions}
        value={(dimension.values as Value[]) ?? []}
        loading={isLoading}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size={inputSize}
            placeholder="Values"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.value}>
            {option.label}
          </li>
        )}
        sx={{ width: "100%" }}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        noOptionsText={isLoading ? "Loading..." : "No options"}
      />
      {loadError && (
        <Typography variant="caption" color="error" sx={{ ml: 1 }}>
          {loadError}
        </Typography>
      )}
    </Box>
  );
}

function DateValueSelect({
  dimension,
  updateDimension,
  filterOption,
}: ValueSelectProps) {
  const valueAsDate =
    typeof dimension.values === "string" ? dayjs(dimension.values) : dayjs();

  // Seed the store with today's date when no value is set yet — otherwise the
  // picker visually shows today but submits an empty value, which renders as a
  // bare predicate in SQL and errors on the backend.
  useEffect(() => {
    if (typeof dimension.values !== "string") {
      updateDimension({ ...dimension, values: dayjs().format("YYYYMMDD") });
    }
    // Mount only, deliberately. This seeds a default the user can then change;
    // re-running it on every `dimension` change would fight their edits, and
    // `updateDimension` is a new identity each render, so including it loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (
    value: dayjs.Dayjs | null,
    keyboardInputValue?: string | undefined
  ) => {
    const dimensionForUpdate = Object.assign({}, dimension);
    dimensionForUpdate.values = value?.format("YYYYMMDD");
    updateDimension(dimensionForUpdate);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        value={valueAsDate}
        onChange={onChange}
        renderInput={(params) => <TextField {...params} size="small" />}
      />
    </LocalizationProvider>
  );
}

function NumberValueSelect({
  dimension,
  updateDimension,
  filterOption,
}: ValueSelectProps) {
  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const dimensionForUpdate = Object.assign({}, dimension);
    dimensionForUpdate.values = [
      { label: event.target.value, value: event.target.value },
    ];
    dimensionForUpdate.values = event.target.valueAsNumber;
    updateDimension(dimensionForUpdate);
  };

  return (
    <TextField
      value={dimension.values}
      type="number"
      onChange={onChange}
      placeholder="Enter value"
      id="valuesSelect"
      size="small"
    />
  );
}
